import { useState, useEffect, useCallback } from 'react';
import { Mail, Linkedin, Calendar, FileText, ThumbsUp, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

interface TimelineEntry {
  type: 'email' | 'linkedin' | 'meeting' | 'activity' | 'endorsement' | 'engagement';
  date: string | null;
  summary: string;
  details: Record<string, any>;
}

interface TimelineResponse {
  contact_id: string;
  total: number;
  entries: TimelineEntry[];
}

const TYPE_CONFIG: Record<string, { color: string; icon: typeof Mail; label: string }> = {
  email: { color: '#3b82f6', icon: Mail, label: 'Email' },
  linkedin: { color: '#22c55e', icon: Linkedin, label: 'LinkedIn' },
  meeting: { color: '#a855f7', icon: Calendar, label: 'Meeting' },
  activity: { color: '#f97316', icon: FileText, label: 'Activity' },
  endorsement: { color: '#eab308', icon: ThumbsUp, label: 'Endorsement' },
  engagement: { color: '#06b6d4', icon: Activity, label: 'Engagement' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

interface Props {
  contactId: string;
}

export default function ContactTimeline({ contactId }: Props) {
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<Set<string>>(new Set(['email', 'linkedin', 'meeting', 'activity', 'endorsement', 'engagement']));

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const typesParam = Array.from(filters).join(',');
      const { data } = await api.get<TimelineResponse>(`/timeline/${contactId}`, {
        params: { types: typesParam, limit: 200 },
      });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [contactId, filters]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const toggleFilter = (type: string) => {
    setFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleExpand = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  return (
    <div>
      {/* Filter toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
          const active = filters.has(type);
          const Icon = cfg.icon;
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body cursor-pointer transition-all"
              style={{
                background: active ? cfg.color + '22' : 'var(--bg-input)',
                border: `1px solid ${active ? cfg.color : 'var(--border-subtle)'}`,
                color: active ? cfg.color : 'var(--text-muted)',
                opacity: active ? 1 : 0.6,
              }}
            >
              <Icon size={12} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <p className="text-sm py-4" style={{ color: 'var(--text-muted)' }}>Loading timeline...</p>
      )}

      {!loading && entries.length === 0 && (
        <p className="text-sm py-4" style={{ color: 'var(--text-muted)' }}>
          No interactions found. Sync Gmail or import LinkedIn data to populate the timeline.
        </p>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        {entries.length > 0 && (
          <div
            className="absolute left-5 top-0 bottom-0 w-px"
            style={{ background: 'var(--border-primary)' }}
          />
        )}

        {entries.map((entry, idx) => {
          const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.activity;
          const Icon = cfg.icon;
          const isExpanded = expanded.has(idx);

          return (
            <div key={idx} className="relative flex gap-4 pb-4">
              {/* Dot */}
              <div
                className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: cfg.color + '22', border: `2px solid ${cfg.color}` }}
              >
                <Icon size={16} style={{ color: cfg.color }} />
              </div>

              {/* Content */}
              <div
                className="flex-1 rounded-lg p-3 cursor-pointer transition-all"
                style={{
                  background: 'var(--bg-panel)',
                  border: '1px solid var(--border-primary)',
                }}
                onClick={() => toggleExpand(idx)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
                      {entry.summary}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {formatDate(entry.date)} {formatTime(entry.date)}
                    </p>
                  </div>
                  <div className="flex-shrink-0 mt-1" style={{ color: 'var(--text-muted)' }}>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && entry.details && (
                  <div
                    className="mt-2 pt-2 text-xs space-y-1"
                    style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                  >
                    {entry.type === 'email' && entry.details.snippet && (
                      <p>{entry.details.snippet}</p>
                    )}
                    {entry.type === 'linkedin' && entry.details.content && (
                      <p>{entry.details.content}</p>
                    )}
                    {entry.type === 'meeting' && (
                      <>
                        {entry.details.location && <p>Location: {entry.details.location}</p>}
                        {entry.details.description && <p>{entry.details.description}</p>}
                        {entry.details.attendees?.length > 0 && (
                          <p>Attendees: {entry.details.attendees.join(', ')}</p>
                        )}
                      </>
                    )}
                    {entry.type === 'activity' && entry.details.description && (
                      <p>{entry.details.description}</p>
                    )}
                    {entry.type === 'endorsement' && entry.details.skill && (
                      <p>Skill: {entry.details.skill}</p>
                    )}
                    {entry.type === 'engagement' && entry.details.signal_type && (
                      <p>Signal: {entry.details.signal_type}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && total > entries.length && (
        <p className="text-xs text-center py-2" style={{ color: 'var(--text-muted)' }}>
          Showing {entries.length} of {total} interactions
        </p>
      )}
    </div>
  );
}
